import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Wordmark } from "@/components/Crest";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Single institutional gateway. One identity, auto-routed by role:
//  applicants → /centre · enrolled families/students → /portal · staff → /admin.
function destFor(role) {
  if (["parent", "student"].includes(role)) return "/portal";
  if (role === "applicant") return "/centre";
  return "/admin";
}

const T = {
  en: {
    title: "Log in", sub: "For Simo applicants, families and students.",
    email: "Email", password: "Password", submit: "Log in",
    prompt: "Not yet applied?", apply: "Start an application →",
    staff: "Simo faculty or administration?", staffLink: "Staff login",
    forgot: "Forgot your password, or have an existing application to activate?",
    forgotTitle: "Access your account", forgotLead: "Enter your email. If we have an account or an application on file, we'll send a secure link to set your password.",
    send: "Send secure link", back: "← Back to log in", sent: "If an account or application exists, a secure link is on its way. Please check your inbox and spam folder.",
  },
  fr: {
    title: "Connexion", sub: "Pour les candidats, familles et étudiants de Simo.",
    email: "E-mail", password: "Mot de passe", submit: "Se connecter",
    prompt: "Pas encore candidat ?", apply: "Commencer une candidature →",
    staff: "Personnel ou administration Simo ?", staffLink: "Connexion du personnel",
    forgot: "Mot de passe oublié, ou une candidature existante à activer ?",
    forgotTitle: "Accédez à votre compte", forgotLead: "Saisissez votre e-mail. Si un compte ou une candidature existe, nous vous enverrons un lien sécurisé pour définir votre mot de passe.",
    send: "Envoyer le lien sécurisé", back: "← Retour à la connexion", sent: "Si un compte ou une candidature existe, un lien sécurisé est en route. Vérifiez votre boîte de réception et vos spams.",
  },
};

export default function Login() {
  const { login } = useAuth();
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const u = await login(email, password);
      nav(next || destFor(u.role));
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const sendForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/portal/forgot", { email }); toast.success(t.sent); setForgot(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      <div className="hidden lg:flex flex-col justify-between bg-ink text-paper p-14">
        <Link to="/" className="flex items-center gap-3"><Wordmark white className="h-11" /></Link>
        <div><p className="font-serif text-4xl leading-snug">Readiness determines progression. Not age.</p><p className="mt-4 text-paper/50">Lux Simonis Populis</p></div>
        <div />
      </div>
      <div className="flex items-center justify-center p-8">
        {!forgot ? (
          <form onSubmit={submit} className="w-full max-w-sm">
            <h1 className="font-serif text-4xl text-ink">{t.title}</h1>
            <p className="mt-2 text-ink/55 text-sm">{t.sub}</p>
            {error && <div data-testid="login-error" className="mt-4 text-sm text-destructive">{error}</div>}
            <div className="mt-6 space-y-3">
              <Input data-testid="login-email" type="email" required placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" />
              <Input data-testid="login-password" type="password" required placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" />
            </div>
            <button data-testid="login-submit" className="mt-6 w-full bg-heraldic text-white py-3 rounded-sm hover:bg-heraldic-700 transition-colors">{t.submit}</button>
            <button type="button" data-testid="login-forgot-link" onClick={() => setForgot(true)} className="mt-4 text-sm text-heraldic hover:underline block">{t.forgot}</button>
            <div className="mt-5 rounded-sm bg-ink/[0.03] border border-border p-3 text-sm text-ink/70" data-testid="login-apply-prompt">
              {t.prompt} <Link to="/apply" className="text-heraldic font-medium">{t.apply}</Link>
            </div>
            <p className="mt-4 text-xs text-ink/45">{t.staff} <Link to="/staff/login" data-testid="login-staff-link" className="text-heraldic">{t.staffLink}</Link></p>
          </form>
        ) : (
          <form onSubmit={sendForgot} className="w-full max-w-sm" data-testid="login-forgot-form">
            <h1 className="font-serif text-3xl text-ink">{t.forgotTitle}</h1>
            <p className="mt-2 text-ink/55 text-sm">{t.forgotLead}</p>
            <Input data-testid="forgot-email" type="email" required placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-6 rounded-sm" />
            <button data-testid="forgot-submit" disabled={busy} className="mt-4 w-full bg-heraldic text-white py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">{t.send}</button>
            <button type="button" onClick={() => setForgot(false)} className="mt-4 text-sm text-ink/55 hover:text-ink block">{t.back}</button>
          </form>
        )}
      </div>
    </div>
  );
}
